import type { ActorKind, MomentCategory } from "./replayClient";

/**
 * The replay's category vocabulary. Every category keeps its word (the feed's
 * Tag, the filter chips, the accessible names) and, since WEB-3366 (owner call
 * reversing the earlier monochrome pass), also wears its own ink on the
 * scrubber ticks and chips: with eight kinds of moment interleaved, hue is
 * what makes the timeline scannable. The word stays primary - the ink never
 * appears without one within reach (chip label, tag, tooltip).
 */
export const CATEGORY_LABEL: Record<MomentCategory, string> = {
  lifecycle: "Lifecycle",
  flow: "Flow",
  capture: "Capture",
  knowledge: "Knowledge",
  work: "Tasks",
  views: "Views",
  pims: "PIMS",
  signal: "Signals",
};

export const ALL_CATEGORIES = Object.keys(CATEGORY_LABEL) as MomentCategory[];

/**
 * Category inks, all existing atlas tokens (tokens.css invents nothing):
 * lifecycle is the visit's own espresso spine; flow moves in working green;
 * capture records in stopped red; knowledge in sage; tasks in flag ochre;
 * views in brian blue (they are Brian's documents, the blue's scoped meaning);
 * pims in the ledger's gold; signals in the ai's indigo accent.
 */
export const CATEGORY_INK: Record<
  MomentCategory,
  { dot: string; border: string }
> = {
  lifecycle: { dot: "bg-ink", border: "border-ink" },
  flow: { dot: "bg-ink-working", border: "border-ink-working" },
  capture: { dot: "bg-ink-stopped", border: "border-ink-stopped" },
  knowledge: { dot: "bg-sage", border: "border-sage" },
  work: { dot: "bg-ink-flag", border: "border-ink-flag" },
  views: { dot: "bg-ink-brian", border: "border-ink-brian" },
  pims: { dot: "bg-gold", border: "border-gold" },
  signal: { dot: "bg-accent", border: "border-accent" },
};

/**
 * The ink for a category off the wire, in plain ink for one this build has never
 * heard of. `MomentCategory` mirrors brian's enum and `toReplay` passes the
 * value straight through, so a category added server-side before a frontend
 * deploy would otherwise take the whole timeline down on `undefined.dot`. Same
 * fail-safe as `toCardSeverity`.
 */
export const categoryInk = (category: MomentCategory) =>
  CATEGORY_INK[category] ?? { dot: "bg-ink", border: "border-ink" };

export const ACTOR_LABEL: Record<ActorKind, string> = {
  user: "Staff",
  agent: "Companion",
  device: "Device",
  system: "System",
};

/**
 * Actor ink on the feed rows' trailing word: agent moments say "Brian did
 * this" in brian blue; people, devices and the system stay quiet annotation.
 * (The scrubber ticks now carry category ink, so the actor distinction lives
 * here, where the ink and the word agree.)
 */
export const ACTOR_INK: Record<ActorKind, { text: string }> = {
  user: { text: "text-annotation" },
  agent: { text: "text-ink-brian" },
  device: { text: "text-annotation" },
  system: { text: "text-annotation" },
};
